import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import ProductCard from "../components/ProductCard";
import GoldCarousel from "../components/GoldCarousel";
import FeaturedReviews from "../components/FeaturedReviews";
import { Button } from "../components/ui/button";
import { ArrowRight, Shield, Truck, RefreshCw, Sparkles } from "lucide-react";

const HERO_BANNERS = [
  {
    url: "https://static.prod-images.emergentagent.com/jobs/e0e48e38-aab1-4b1a-ba83-c9595b6e19df/images/487a549f78b4611e3603acb3fc785836f9a0c1034e195596fcad4d023c69fd64.png",
    eyebrow: "Daily Wear · Light & Joyful",
    title: "Jewellery that lives with you",
    desc: "From morning chai to late-night chai — pieces meant for every moment of your day.",
    cta: { label: "Shop Daily Wear", to: "/products?collection=Daily%20Wear" },
  },
  {
    url: "https://static.prod-images.emergentagent.com/jobs/e0e48e38-aab1-4b1a-ba83-c9595b6e19df/images/434eedfa596665653eb491a167f35a6f0ac1d7c5fcb6e3c942e35d866fad7308.png",
    eyebrow: "The Bridal Edit",
    title: "Heirlooms for the new bride",
    desc: "Hand-crafted mangalsutras, temple necklaces, and bridal sets — passed down generations.",
    cta: { label: "Explore Bridal", to: "/products?collection=Bridal" },
  },
];

const CATEGORIES = [
  { slug: "rings", name: "Rings", img: "https://static.prod-images.emergentagent.com/jobs/e0e48e38-aab1-4b1a-ba83-c9595b6e19df/images/e6f325ddff390482870261f4880d7c889ba8a777e06bd21729c033c46778f39a.png" },
  { slug: "bangles", name: "Bangles", img: "https://static.prod-images.emergentagent.com/jobs/e0e48e38-aab1-4b1a-ba83-c9595b6e19df/images/deb7bd113cc7ff2732ca3e242a654c4a2f23a2344739cc7f2717e61857c184a5.png" },
  { slug: "necklaces", name: "Necklaces", img: "https://images.unsplash.com/photo-1758995115518-26f90aa61b97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBnb2xkJTIwamV3ZWxyeSUyMG5lY2tsYWNlfGVufDB8fHx8MTc3OTM1MTE4OHww&ixlib=rb-4.1.0&q=85" },
  { slug: "mangalsutra", name: "Mangalsutra", img: "https://images.unsplash.com/photo-1769706039344-7ad8d7ec2442?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxJbmRpYW4lMjBnb2xkJTIwamV3ZWxyeSUyMG5lY2tsYWNlfGVufDB8fHx8MTc3OTM1MTE4OHww&ixlib=rb-4.1.0&q=85" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/products?featured=true&limit=8`).then(r => setFeatured(r.data?.items || []));
    const t = setInterval(() => setHeroIdx(i => (i + 1) % HERO_BANNERS.length), 7000);
    return () => clearInterval(t);
  }, []);

  const hero = HERO_BANNERS[heroIdx];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="hero-pattern">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center py-12 lg:py-20">
            <div className="lg:col-span-5 fade-in" key={`txt-${heroIdx}`}>
              <p className="label-small text-[#8B1E28] mb-4">{hero.eyebrow}</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C2A29] leading-[1.05]" data-testid="hero-title">
                {hero.title}
              </h1>
              <p className="mt-6 text-[#595552] text-base leading-relaxed max-w-md">
                {hero.desc}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={hero.cta.to}>
                  <Button className="btn-primary px-6 py-6 h-auto rounded-full" data-testid="hero-cta">
                    {hero.cta.label} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline" className="btn-gold px-6 py-6 h-auto rounded-full border-[#C5A059] text-[#8B1E28]">
                    Shop the full edit
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex gap-2 items-center">
                {HERO_BANNERS.map((_, i) => (
                  <button key={i} onClick={() => setHeroIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === heroIdx ? "w-10 bg-[#8B1E28]" : "w-2 bg-[#C5A059]/40"}`}
                    data-testid={`hero-dot-${i}`} />
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 fade-in" key={`img-${heroIdx}`}>
              <div className="relative">
                <div className="aspect-[5/4] rounded-3xl overflow-hidden bg-[#FFF5F3] border border-[#EAE3D9]">
                  {/* Hero image is above-the-fold — fetch eagerly with high priority
                      so LCP paints as fast as possible. Non-active banners are
                      preloaded via <link rel="preload"> in index.html if needed. */}
                  <img
                    src={hero.url}
                    alt="Sumanya hero"
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                  />
                </div>
                <div className="hidden md:block absolute -bottom-6 -left-6 bg-white border border-[#EAE3D9] rounded-2xl p-4 shadow-lg max-w-[230px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-4 w-4 text-[#C5A059]" />
                    <span className="label-small text-[#8A8480]">Made in India</span>
                  </div>
                  <p className="text-sm text-[#2C2A29]">BIS Hallmarked · 100% certified gold</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-[#EAE3D9] bg-white" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 py-8">
          {[
            { Icon: Shield, t: "BIS Hallmarked", s: "100% certified" },
            { Icon: Truck, t: "Free Shipping", s: "On orders above ₹2,500" },
            { Icon: RefreshCw, t: "Lifetime Exchange", s: "On all gold items" },
            { Icon: Sparkles, t: "Made by Artisans", s: "From Jaipur, Kolkata, Surat" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <f.Icon className="h-6 w-6 text-[#C5A059] shrink-0" />
              <div>
                <div className="text-sm font-semibold text-[#2C2A29]">{f.t}</div>
                <div className="text-xs text-[#8A8480]">{f.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CINEMATIC GOLD CAROUSEL */}
      <GoldCarousel />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <p className="label-small text-[#C5A059] mb-3">Browse by Category</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2C2A29]">Find your everyday treasure</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="categories-grid">
          {CATEGORIES.map(c => (
            <Link key={c.slug} to={`/products/${c.slug}`}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden hover-zoom-wrap border border-[#EAE3D9]"
              data-testid={`category-${c.slug}`}>
              <img src={c.img} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C2A29]/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                <h3 className="font-display text-2xl">{c.name}</h3>
                <span className="text-xs label-small group-hover:text-[#E8A99E] transition-colors">Shop now →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-[#FFF5F3]/40 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="label-small text-[#C5A059] mb-3">The Sumanya Edit</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2C2A29]">Loved by our patrons</h2>
            </div>
            <Link to="/products" className="text-[#8B1E28] font-medium text-sm hover:underline" data-testid="view-all-products">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="featured-grid">
            {featured.map(p => <ProductCard key={p.product_id} p={p} />)}
          </div>
        </div>
      </section>

      {/* FEATURED REVIEWS (5★ social proof) */}
      <FeaturedReviews />

      {/* STORY / CULTURE STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="divider-paisley mb-8">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
          </svg>
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2C2A29] max-w-3xl mx-auto leading-tight">
          “Jewellery isn't just adornment — it's memory, ritual, and love passed down.”
        </h2>
        <p className="text-[#595552] mt-6 max-w-2xl mx-auto">
          Sumanya is a love-letter to the women of India — your grandmother's nath, your mother's mangalsutra, your sister's first pair of jhumkas.
        </p>
        <Link to="/products" className="inline-block mt-8">
          <Button className="btn-primary rounded-full px-8 py-6 h-auto">Begin your story</Button>
        </Link>
      </section>
    </div>
  );
}
